import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import { useTheme, useMediaQuery, Box,
  CircularProgress, Typography, Tooltip, IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const SupplyStandards = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "SupplyStandards", "create", false);
  const canEdit   = hasPermission(user?.permissions, "SupplyStandards", "update", false);
  const canDelete = hasPermission(user?.permissions, "SupplyStandards", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [supplyStandards, setSupplyStandards] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const handleSupplyStandards = async () => {
      try {
        setError(null);
        setLoading(true);
        const res = await api.get('/supplystandards');
        setSupplyStandards(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handleSupplyStandards();
  }, []);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, supplyStandards);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/supplystandards/delete/${idToDelete}`);
        setSupplyStandards(supplyStandards.filter((item) => item._id !== idToDelete));
        toast.success('Supply standard deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, supplyStandards);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/supplystandards/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setSupplyStandards(supplyStandards.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error("Failed to delete: " + err.message);
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, supplyStandards]);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(isMobile ? { width: 60 } : { flex: 0.6 }),
    },
    {
      field: "supplyStandardName",
      headerName: "SupplyStandardName",
      ...(isMobile ? { width: 200 } : { flex: 3 }),
    },
    {
      field: 'sectors',
      headerName: 'Sectors',
      ...(isMobile ? { width: 100 } : { flex: 1.2 }),
      renderCell: (params) => (
        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
          {params.row?.sectors?.map((sector, index) => (
            <FlexBetween key={index} gap={1}>
              <Typography variant="body2">{sector}</Typography>
            </FlexBetween>
          ))}
        </Box>
      ),
    },
    {
      field: 'departureAirports',
      headerName: 'Departure',
      ...(isMobile ? { width: 60 } : { flex: 1 }),
      renderCell: (params) => (
        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
          {params.row?.departureAirports?.map((airport, index) => (
            <FlexBetween key={index} gap={1}>
              <Typography variant="body2" sx={{ textAlign: 'left' }}>{airport}</Typography>
            </FlexBetween>
          ))}
        </Box>
      ),
    },
    {
      field: "minBlockTime",
      headerName: "Min BH",
      ...(isMobile ? { width: 60 } : { flex: 1 }),
    },
    {
      field: 'products',
      headerName: 'Products',
      ...(isMobile ? { width: 200 } : { flex: 3.5 }),
      renderCell: (params) => (
        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
          {params.row.products?.map((product, index) => (
            <FlexBetween key={index} gap={1}>
              <Typography variant="body2">{product.productName}</Typography>
              <Typography variant="body2">{product.quantity}</Typography>
            </FlexBetween>
          ))}
        </Box>
      ),
    },
    {
      field: "sortOrder",
      headerName: "SortOrder",
      ...(isMobile ? { width: 100 } : { flex: 1 }),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(isMobile ? { width: 100 } : { flex: 1.2 }),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canEdit) return; navigate(`/supplystandards/${params.row._id}`); }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canDelete) return; handleDeleteClick(params.row._id); }}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];
  }, [navigate, theme.palette, canEdit, canDelete]);

  const columnsToShow = [...columns, ...actionColumn];

  const gridStyles = {
    "& .MuiDataGrid-row.isActive-false": {
      backgroundColor: `${theme.palette.primary.light} !important`,
    },
  };


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Supply Standards"
          subtitle={`${loading ? 'Loading...' : 'List of Supply Standards'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, supplyStandards)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/supplystandards/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
          sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
            {isMobile ? (
              <CardList
                rows={supplyStandards}
                primaryField="supplyStandardName"
                fields={[
                  { label: "Sectors", key: "sectors" },
                  { label: "Airports", key: "departureAirports" },
                  { label: "Products", key: "products" },
                  { label: "Minimum BH", key: "minBlockTime" },
                  { label: "Sort Order", key: "sortOrder" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/supplystandards/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={supplyStandards || []}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                  sx: gridStyles,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default SupplyStandards;
